import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { booleanColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { Contact } from "src/api/generated/erp/db/types/tables/contact.ts";
import { ContactConnectedItemType } from "src/api/generated/erp/db/types/enums/contactConnectedItemType.ts";
import { ContactForm } from "src/components/views/erp/contacts/ContactForm.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getContactEndpoints } from "src/components/views/erp/contacts/contactEndpoints.ts";

export interface ContactsDataGridProps {
	connectedItemType: ContactConnectedItemType;
	connectedItemId: number;
	editable?: boolean;
	/** Called after contacts are added, edited or deleted. */
	onContactsChanged?: () => Promise<unknown> | void;
}

export const ContactsDataGrid = ({
	connectedItemType,
	connectedItemId,
	editable = true,
	onContactsChanged,
}: ContactsDataGridProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const showDefaultContactField =
		connectedItemType === "SALES_LEAD" ||
		(connectedItemType === "CUSTOMER" && tenantConfig.erp.customerFormUseDefaultContact);
	const contactEndpoints = getContactEndpoints(connectedItemType, connectedItemId);

	return (
		<ClientSideDataGridModel
			customFunctionUiActionView={"CONTACTS"}
			gridId={"E0CCF741533BF1D0FF"}
			fetchData={() => contactEndpoints.getContacts()}
			getRows={(data) => data}
			getRowId={(row) => row.contactId}
			initialParams={{}}
			render={({ dataGridProps, refreshData }) => {
				return (
					<FormCrudDataGrid<Contact>
						columns={[
							textColumn({
								field: "name",
								headerName: i18n.t("name"),
								width: 200,
							}),
							textColumn({
								field: "designation",
								headerName: i18n.t("designation"),
								width: 120,
							}),
							textColumn({
								field: "email",
								headerName: i18n.t("email"),
								width: 120,
							}),
							textColumn({
								field: "phone",
								headerName: i18n.t("phone"),
								width: 120,
							}),
							textColumn({
								field: "note",
								headerName: i18n.t("note"),
								width: 500,
							}),
							...(showDefaultContactField ?
								[
									booleanColumn<Contact>({
										field: "isDefaultContact",
										headerName: i18n.t("default_contact"),
										width: 120,
									}),
								]
							:	[]),
						]}
						form={{
							addRowEnabled: editable,
							editEnabled: editable,
							dialogSize: "sm",
							dialogTitle: i18n.t("contact"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.contacts.contact.formDialog"),
								pathSegments:
									row == null ? [i18n.t("contact")] : [`${i18n.t("contact")} ${row.contactId}`],
								data: {
									connectedItemType,
									connectedItemId,
									contactId: row?.contactId ?? null,
								},
							}),
							component: (props) => {
								const { row, onCompleted, onFormEdited } = props;
								return (
									<ContactForm
										connectedItemId={connectedItemId}
										connectedItemType={connectedItemType}
										contact={row}
										onCompleted={onCompleted}
										onFormEdited={onFormEdited}
									/>
								);
							},
						}}
						remove={
							editable ?
								{
									type: "enabled",
									action: async ({ items }) => {
										await contactEndpoints.delete(items[0]!.contactId);
									},
								}
							:	undefined
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.contacts.contacts.grid"),
							data: {
								connectedItemType,
								connectedItemId,
							},
							rowFields: ["contactId", "connectedItemType", "connectedItemId"],
							selectedRowPathSegment: (row) => `${i18n.t("contact")} ${row.contactId}`,
						}}
						{...dataGridProps}
						refreshData={async () => {
							await Promise.all([refreshData(), onContactsChanged?.()]);
						}}
					/>
				);
			}}
		/>
	);
};
